import { DELETE_COLOR } from "@constants/colors"
import { useUserPageContext } from "@contexts/main/users_page_context"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { IconButton } from "@mui/material"
import Box from "@mui/material/Box"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { formatDate } from "@utils/functions/formatDate"
import { useState } from "react"

import PopperMenu from "./sub_components/popper_menu"

import DeleteUser from "@components/dialogs/delete_user"
import UpdateUser from "@components/dialogs/update_user"

import DataTableToolbar from "@components/data_table_toolbar"
import GridTable from "@components/grid-table"
import { RoleTypes, User } from "__generated__/graphql"
import styles from "./styles"

export type DataTableRow = {
  _id: string
  name: string
  email: string
  phone: string
  role: string
  createdAt: string
}

export default function UsesrDataTable() {
  const {
    translate,
    usersData,
    setSearch,
    isLoading,
    pagination,
    setPagination
  } = useUserPageContext()

  const [clickedRow, setClickedRow] = useState<User>()
  const [isEditOpen, setIsEditOpen] = useState(false)

  const [optionAnchorEl, setOptionAnchorEl] =
    useState<HTMLButtonElement | null>(null)

  function handlePopClose() {
    setOptionAnchorEl(null)
  }
  function handleOptionClick(
    event: React.MouseEvent<HTMLButtonElement>,
    row: User
  ) {
    setOptionAnchorEl(event.currentTarget)
    setClickedRow(row)
  }

  function handleClickedRow(row: DataTableRow) {
    setClickedRow({ ...row, role: row.role as RoleTypes })
  }
  function handleEditClose() {
    setIsEditOpen(false)
  }
  function handleEditOpen() {
    setIsEditOpen(true)
  }
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  function handleDeleteClose() {
    setIsDeleteOpen(false)
  }
  function handleDeleteOpen() {
    setIsDeleteOpen(true)
  }

  const options = [
    {
      headerName: translate("edit"),
      color: "#000",
      action: handleEditOpen
    },
    {
      headerName: translate("delete"),
      color: DELETE_COLOR,
      action: handleDeleteOpen
    }
  ]

  const fieldWidth = 180

  const columns: GridColDef[] = [
    {
      field: "name",
      minWidth: fieldWidth,
      description: "fixed",
      headerName: translate("name"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("name")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string
        const row = params.row as DataTableRow
        return (
          <Box
            sx={styles.renderCellIconBox}
            onClick={() => handleClickedRow(row)}
          >
            <Box>
              {value.length > 30 ? value?.substring(0, 30) + "..." : value}
            </Box>
          </Box>
        )
      },

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "email",
      minWidth: fieldWidth,

      headerName: translate("email"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("email")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return <Box sx={styles.renderCellIconBox}>{params.value}</Box>
      },

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "phone",
      minWidth: fieldWidth,

      headerName: translate("phone"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("phone")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return <Box sx={styles.renderCellIconBox}>{params.value}</Box>
      },

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "role",
      minWidth: fieldWidth,

      headerName: translate("role"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("role")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.value
            ? params.value?.charAt(0) + params.value?.substring(1).toLowerCase()
            : ""}
        </Box>
      ),

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "createdAt",
      minWidth: fieldWidth,
      headerName: translate("created_date"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("created_date")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {formatDate(translate("date_format"), params.value as string)}
        </Box>
      ),

      disableColumnMenu: true
    },
    {
      field: "actions",
      minWidth: 80,
      headerName: "Actions",
      renderHeader: () => <Box></Box>,
      renderCell: (params: GridRenderCellParams) => {
        const row = usersData?.find((item) => item._id === params.id) as User
        return (
          <Box>
            <IconButton onClick={(e) => handleOptionClick(e, row)}>
              <MoreVertIcon />
            </IconButton>
            <PopperMenu
              open={!!optionAnchorEl}
              options={options}
              anchorEl={optionAnchorEl}
              onClose={handlePopClose}
            />
          </Box>
        )
      },
      disableColumnMenu: true,
      sortable: false,
      flex: 0.5,
      align: "right",
      headerAlign: "right"
    },
    {
      field: "rightIcon",
      minWidth: 50,
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      flex: 0.1,
      align: "right",
      headerAlign: "right"
    }
  ]

  return (
    <Box sx={{ overflow: "hidden", height: "100%", p: 2 }}>
      <DataTableToolbar onSearch={setSearch} />
      <GridTable
        sortable
        loading={isLoading}
        rows={usersData as unknown as DataTableRow[]}
        columns={columns}
        rowCount={(usersData as unknown as DataTableRow[])?.length}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row: DataTableRow) => row._id}
        disableColumnSelector={true}
      />
      <DeleteUser
        isOpen={isDeleteOpen}
        setClose={handleDeleteClose}
        selectedRowId={clickedRow?._id as string}
      />
      <UpdateUser
        isOpen={isEditOpen}
        setClose={handleEditClose}
        selectedRow={clickedRow}
      />
    </Box>
  )
}
